import { useState } from "react";
import {
  KeyRound,
  LockKeyhole,
  Copy,
  Check,
  Eye,
  EyeOff,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader, cardClass, primaryButtonClass, secondaryButtonClass } from "@/components/shell/PageHeader";
import { useUniversalKey } from "@/hooks/useUniversalKey";

const formatDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "Not available";

const PROVIDERS = [
  { id: "openai" as const, letter: "G", name: "GPT Models", description: "OpenAI's advanced reasoning models", field: "provider_openai" as const },
  { id: "anthropic" as const, letter: "C", name: "Claude Models", description: "Anthropic's advanced reasoning models", field: "provider_anthropic" as const },
];

const UniversalAIKeyPage = () => {
  const [infoOpen, setInfoOpen] = useState(false);
  const {
    keyData,
    loading,
    copied,
    showRegenConfirm,
    setShowRegenConfirm,
    showFullKey,
    setShowFullKey,
    providerLoading,
    isRegenerating,
    isToggling,
    displayKey,
    handleCopy,
    handleRegenerate,
    handleToggle,
    handleProviderToggle,
  } = useUniversalKey(true);

  const statusLabel = !keyData ? (loading ? "Loading…" : "No key issued") : keyData.is_active ? "Active" : "Disabled";
  const statusTone = !keyData
    ? "border-line bg-soft text-muted-foreground"
    : keyData.is_active
      ? "border-success/25 bg-success-bg text-success"
      : "border-danger/25 bg-danger-bg text-danger";

  return (
    <div className="mx-auto w-full max-w-[1080px]">
      <PageHeader
        eyebrow="Your account"
        title="Universal AI Key"
        description="One Joylo-issued key lets your apps use AI models. No separate provider account needed."
      />

      <section className={`${cardClass} p-6`}>
        <div className="flex items-start gap-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-selected">
            <KeyRound className="h-[22px] w-[22px] text-plum" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-foreground">Your AI key</h2>
            <p className="mt-1 text-[14px] text-muted-foreground">Add AI features to the apps you build using a key issued by Joylo.</p>
          </div>
          <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[12px] font-semibold ${statusTone}`}>{statusLabel}</span>
        </div>

        {loading && !keyData ? (
          <div className="mt-5 flex items-center gap-3 rounded-xl border border-dashed border-line bg-soft px-4 py-4 text-[14px] text-muted-foreground">
            <Loader2 className="h-[18px] w-[18px] animate-spin" />
            Loading Universal AI Key…
          </div>
        ) : keyData ? (
          <>
            <div className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-soft px-4 py-3">
              <code className="min-w-0 flex-1 truncate font-mono text-[14px] text-foreground">{displayKey}</code>
              <button
                type="button"
                onClick={() => setShowFullKey((v) => !v && !!keyData?.key_value)}
                disabled={!keyData?.key_value}
                title={showFullKey ? "Hide key" : "Show key"}
                className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-surface hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              >
                {showFullKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                title="Copy key"
                className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
              >
                {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            {keyData.is_new && keyData.key_value && (
              <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-warning/25 bg-warning-bg px-4 py-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <p className="text-[13px] font-medium text-warning">
                  Please save this secret key somewhere safe and accessible. For security reasons, you won't be able to view it again through your account. If you lose this secret key, you'll need to generate a new one.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="mt-5 flex items-center gap-3 rounded-xl border border-dashed border-line bg-soft px-4 py-4 text-[14px] text-muted-foreground">
            <LockKeyhole className="h-[19px] w-[19px]" />
            No Universal AI Key has been issued
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-muted-foreground">Manage one key here and use it across the apps you build.</p>
          <div className="flex flex-wrap items-center gap-2.5">
            <button type="button" onClick={() => setInfoOpen(true)} className={secondaryButtonClass}>
              How this works
            </button>
            {keyData && (
              <>
                <button
                  type="button"
                  onClick={handleToggle}
                  disabled={isToggling}
                  className={`${secondaryButtonClass} ${keyData.is_active ? "text-danger" : "text-success"}`}
                >
                  {isToggling ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : keyData.is_active ? (
                    <ToggleRight className="h-4 w-4" />
                  ) : (
                    <ToggleLeft className="h-4 w-4" />
                  )}
                  {keyData.is_active ? "Disable key" : "Enable key"}
                </button>
                {!showRegenConfirm ? (
                  <button type="button" onClick={() => setShowRegenConfirm(true)} disabled={isRegenerating} className={secondaryButtonClass}>
                    <RefreshCw className="h-4 w-4" />
                    Regenerate
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={handleRegenerate} disabled={isRegenerating} className={primaryButtonClass}>
                      {isRegenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm regenerate"}
                    </button>
                    <button type="button" onClick={() => setShowRegenConfirm(false)} className={secondaryButtonClass}>
                      Cancel
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className={`${cardClass} p-6`}>
          <h2 className="font-display text-[16px] font-semibold tracking-[-0.02em] text-foreground">Keep your key secure</h2>
          <ul className="mt-4 space-y-3">
            {[
              "Use your key only in applications you trust.",
              "Keep it out of shared files, public code and screenshots.",
              "Replace your key if you think it has been exposed.",
            ].map((tip) => (
              <li key={tip} className="flex items-start gap-2.5 text-[14px] text-foreground">
                <Check className="mt-0.5 h-[17px] w-[17px] shrink-0 text-success" />
                {tip}
              </li>
            ))}
          </ul>
        </section>

        <section className={`${cardClass} p-6`}>
          <h2 className="font-display text-[16px] font-semibold tracking-[-0.02em] text-foreground">Key status</h2>
          <dl className="mt-4 divide-y divide-line">
            {[
              ["Universal AI Key", statusLabel],
              ["Created", formatDate(keyData?.created_at)],
              ["Last used", formatDate(keyData?.last_used_at)],
              ["Last regenerated", formatDate(keyData?.regenerated_at)],
            ].map(([term, value]) => (
              <div key={term} className="flex items-center justify-between gap-4 py-2.5 text-[14px]">
                <dt className="text-muted-foreground">{term}</dt>
                <dd className="font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className={`${cardClass} mt-4 p-6`}>
        <h2 className="font-display text-[16px] font-semibold tracking-[-0.02em] text-foreground">AI providers</h2>
        <p className="mt-1 text-[14px] text-muted-foreground">
          Select which AI providers this universal AI key can access. Any requests made to disabled providers will be rejected automatically.
        </p>
        <div className="mt-4 divide-y divide-line overflow-hidden rounded-xl border border-line">
          {PROVIDERS.map((provider) => {
            const enabled = !!keyData?.[provider.field];
            return (
              <div key={provider.id} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="flex items-center gap-3.5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-line bg-soft font-display text-[16px] font-bold text-foreground">
                    {provider.letter}
                  </span>
                  <div>
                    <p className="text-[14px] font-semibold text-foreground">{provider.name}</p>
                    <p className="text-[12px] text-muted-foreground">{provider.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`${provider.name} access`}
                  onClick={() => handleProviderToggle(provider.id, !enabled)}
                  disabled={!keyData || providerLoading === provider.id}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? "bg-gradient-sunset" : "bg-line"}`}
                >
                  {providerLoading === provider.id ? (
                    <Loader2 className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
                  ) : (
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-w-md rounded-[18px] border-line bg-surface">
          <DialogHeader>
            <DialogTitle className="font-display text-[20px] tracking-[-0.02em]">One key for AI in your apps</DialogTitle>
            <DialogDescription>Joylo manages access to supported AI models for you.</DialogDescription>
          </DialogHeader>
          <p className="text-[14px] leading-relaxed text-muted-foreground">
            Joylo issues a Universal AI Key that your apps can use to call supported AI models. You manage the key in your account, without setting up a separate account with each model provider.
          </p>
          <button type="button" onClick={() => setInfoOpen(false)} className={primaryButtonClass}>
            Got it
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UniversalAIKeyPage;
